import { Collapse } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import FoldedHandsSVG from '../../../../assets/icons/FoldedHands';
import MagicStarsSvg from '../../../../assets/icons/MagicStarsSvg';
import theme from '../../../../assets/theme';
import useAuthGuardPrompt from '../../../../hooks/useAuthGuardPrompt';
import { useMetrics } from '../../../../hooks/useMetrics';
import usePaywallPrompt from '../../../../hooks/usePaywallPrompt';
import { useUploadPresentation } from '../../../../hooks/useUploadPresentation';
import { PromptInput } from '../../../../services/backendService/types';
import {
  MAX_LIMIT_LEARNING_OBJECTIVE,
  MAX_LIMIT_PROMPT,
} from '../../../../utils/aiGeneratorUtils';
import { debounceEventIfNotTrusted } from '../../../../utils/general';
import { LicenseCoarseLevels } from '../../../../utils/tenantPlanUtils';
import { Input } from '../../../_atoms/_Input';
import { Loader } from '../../../_atoms/_Loader';
import { TextArea } from '../../../_atoms/_TextArea';
import { Button } from '../../../_atoms/Button';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import BaseModal from '../../../_molecules/BaseModal';
import PresentationFileSelector from '../../../_molecules/PresentationFileSelector';
import { GeneratorDropdown } from './GeneratorDropdown';

const InputElement = ({
  promptInput,
  onChange,
  promptInputSuggestions,
  value,
}: {
  promptInput: PromptInput;
  promptInputSuggestions?: PromptInput[];
  onChange: (val: number | string | { filePath: string; fileType: string }) => void;
  value?: string | number | { filePath: string; fileType: string };
}) => {
  const renderedSuggestion = useSinglePromptinputSuggestion({
    promptInput,
    promptInputSuggestions: promptInputSuggestions,
    value,
  });
  if (promptInput.type === 'shortText') {
    return (
      <Input
        style={{ width: '100%' }}
        type="text"
        value={value as string | undefined}
        label={promptInput.label}
        placeholder={renderedSuggestion as string}
        labelStyle={{ fontWeight: 'bold', color: theme.colors.black }}
        maxLength={promptInput.constraints?.maxLength || MAX_LIMIT_PROMPT}
        error={
          value &&
          (value as string).length >=
            (promptInput.constraints?.maxLength || MAX_LIMIT_PROMPT)
            ? `${(value as string).length}/${promptInput.constraints?.maxLength ||
                MAX_LIMIT_PROMPT}`
            : undefined
        }
        onChange={e => onChange(e.target.value)}
      />
    );
  }
  if (promptInput.type === 'longText') {
    return (
      <TextArea
        style={{ width: '100%' }}
        label={promptInput.label}
        labelStyle={{ fontWeight: 'bold', color: theme.colors.black }}
        rows={3}
        placeholder={renderedSuggestion}
        symbolCount={true}
        maxLength={promptInput.constraints?.maxLength || MAX_LIMIT_LEARNING_OBJECTIVE}
        value={value as string | undefined}
        error={
          value &&
          (value as string).length >=
            (promptInput.constraints?.maxLength || MAX_LIMIT_LEARNING_OBJECTIVE)
            ? `${(value as string).length}/${promptInput.constraints?.maxLength ||
                MAX_LIMIT_LEARNING_OBJECTIVE}`
            : undefined
        }
        textAreaStyled={{ minHeight: '100px' }}
        onChange={e => onChange(e.target.value)}
      />
    );
  }
  if (promptInput.type === 'dropdown') {
    return (
      <GeneratorDropdown
        label={promptInput.label}
        value={value as string | undefined}
        options={promptInput.constraints?.options || []}
        onSelect={e => onChange(e as string | number)}
      ></GeneratorDropdown>
    );
  }

  if (promptInput.type == 'slidesUpload') {
    return (
      <InputFileElement
        onChange={onChange}
        label={promptInput.label}
        value={value as { filePath: string; fileType: string } | undefined}
      />
    );
  }

  return null;
};

const InputFileElement = ({
  onChange,
  value,
  label,
}: {
  value: { filePath: string; fileType: string } | undefined;
  onChange: (val: number | string | { filePath: string; fileType: string }) => void;
  label: string;
}) => {
  const {
    fileInfo,
    upload: uploadPresentation,
    isUploading: isUploadingFile,
    isSuccess: uploadPresentationSuccess,
    isError: isErrorUploadingFile,
    reset: resetFileUpload,
  } = useUploadPresentation();
  const [fileSizeExceeded, setFileSizeExceeded] = useState(false);

  const [uploadedFile, setUploadedFile] = useState<
    | {
        filePath: string;
        fileType: string;
      }
    | undefined
  >(value);
  const handleFileSelected = useCallback(
    (file: File) => {
      const fileSizeInMb = file.size * 1e-6;
      if (fileSizeInMb <= 100) {
        setFileSizeExceeded(false);
        uploadPresentation({ file });
      }

      if (fileSizeInMb > 100) {
        setFileSizeExceeded(true);
      }
    },
    [uploadPresentation],
  );

  useEffect(() => {
    if (uploadPresentationSuccess && fileInfo) {
      setUploadedFile(fileInfo);
      resetFileUpload();
      onChange(fileInfo);
    }
  }, [fileInfo, onChange, uploadPresentationSuccess, resetFileUpload]);
  return (
    <FileUploadArea
      handleFileSelected={handleFileSelected}
      isUploadingFile={isUploadingFile}
      uploadedFile={uploadedFile}
      setUploadedFile={setUploadedFile}
      isErrorUploadingFile={isErrorUploadingFile}
      resetFileUpload={resetFileUpload}
      fileSizeExceeded={fileSizeExceeded}
      label={label}
    />
  );
};

export type GeneratorFormState = {
  [inputId: string]: string | number | { filePath: string; fileType: string };
};

const generateFormState = (promptInputs: PromptInput[]): GeneratorFormState => {
  return promptInputs.reduce((acc, promptInput) => {
    return {
      ...acc,
      [promptInput.id]: promptInput.defaultValue || '',
    };
  }, {});
};

export const GeneratorForm = ({
  promptInputs,
  handleInputChange,
  handleSubmit,
  isLoading,
  isError,
  errorMessage,
  formState,
  promptInputSuggestions,
  isPremiumGenerator,
  generatorPlan,
  generatorId,
  upgradeLocation,
  preFillSearch,
}: {
  promptInputs: PromptInput[];
  promptInputSuggestions?: PromptInput[][];
  handleInputChange: (
    inputId: string,
    value: string | number | { filePath: string; fileType: string },
  ) => void;
  handleSubmit: () => void;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  formState?: GeneratorFormState;
  isPremiumGenerator?: boolean | null;
  generatorPlan?: LicenseCoarseLevels | null;
  generatorId?: string;
  upgradeLocation?: string;
  preFillSearch?: string;
}) => {
  const [showErrorModal, setShowErrorModal] = useState(false);
  const metrics = useMetrics();
  const { tenantPlan, isReady, promptPaywall, paywallComponent } = usePaywallPrompt({
    startState: { screen: 'chooseProduct' },
  });
  const { authGuardComponent, showAuthGuard } = useAuthGuardPrompt(
    undefined,
    undefined,
    generatorId,
  );
  const requiredInputs = useMemo(() => promptInputs.filter(p => p.isRequired), [
    promptInputs,
  ]);
  const optionalInputs = useMemo(() => promptInputs.filter(p => !p.isRequired), [
    promptInputs,
  ]);
  const currentPromptInputSuggestions = usePromptInputSuggestions({
    inputSuggestions: promptInputSuggestions,
    formState,
    promptInputs,
  });

  useEffect(() => {
    if (isError) {
      setShowErrorModal(true);
    }
  }, [isError]);

  //Pre fill form with search
  useEffect(() => {
    if (preFillSearch) {
      const topicInput = promptInputs.find(p => p.label.includes('topic'));
      if (topicInput) {
        handleInputChange(topicInput.id, preFillSearch);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preFillSearch]);

  const isFormEnabled = useMemo(() => {
    if (formState) {
      return requiredInputs.every(input => {
        const realValue = formState?.[input.id];
        return realValue && realValue !== '';
      });
    }
    return false;
  }, [requiredInputs, formState]);

  return (
    <FormContainer>
      {paywallComponent}
      {authGuardComponent}
      <BaseModal visible={showErrorModal} onCancel={() => setShowErrorModal(false)}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <IconWrapper height="120px" width="120px" style={{ marginTop: 20 }}>
            <FoldedHandsSVG />
          </IconWrapper>

          <ErrorMessage>
            {isError && (
              <>
                <ErrorMessage>{'Oops, not able to generate.'}</ErrorMessage>
                {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
                {!errorMessage && (
                  <ErrorMessage style={{ fontSize: '16px' }}>
                    {
                      'Please try again with different phrasing, clearer language, or shorter inputs. Alternatively, you can explore other customizable lessons that may better suit your needs.'
                    }
                  </ErrorMessage>
                )}
              </>
            )}
          </ErrorMessage>
          <Button
            styles={{ marginTop: 20 }}
            width="120px"
            onClick={() => setShowErrorModal(false)}
          >
            Ok
          </Button>
        </div>
      </BaseModal>
      <h3>Customize this lesson</h3>
      <GridContainer>
        {requiredInputs?.map(promptInput => (
          <InputElement
            key={promptInput.id}
            promptInput={promptInput}
            promptInputSuggestions={currentPromptInputSuggestions}
            value={formState?.[promptInput.id]}
            onChange={val => handleInputChange(promptInput.id, val)}
          />
        ))}

        {optionalInputs.length > 0 && (
          <OptionalInputCollapse ghost style={{ width: '100%' }}>
            <OptionalInputCollapsePanel
              header={'Optional inputs'}
              key="optional"
              style={{ color: theme.colors.black, fontWeight: 'bold' }}
            >
              <GridContainer>
                {optionalInputs?.map(promptInput => (
                  <InputElement
                    key={promptInput.id}
                    value={formState?.[promptInput.id]}
                    promptInputSuggestions={currentPromptInputSuggestions}
                    promptInput={promptInput}
                    onChange={val => handleInputChange(promptInput.id, val)}
                  />
                ))}
              </GridContainer>
            </OptionalInputCollapsePanel>
          </OptionalInputCollapse>
        )}
      </GridContainer>
      {generatorPlan === LicenseCoarseLevels.enterprise && !tenantPlan.isEnterprise() ? (
        <CreateButton
          $color={theme.colors.blue}
          styles={{ width: '100%' }}
          type="primary"
          loading={isLoading}
          onClick={() => {
            metrics.logEvent('Enterprise.Generator.Upgrade.Clicked', {
              location: upgradeLocation,
              generatorId,
            });
            window.open(
              `https://share.hsforms.com/1Nu2xHyieQGWHbgJ-jZff2g4evga`,
              '__blank',
            );
            return;
          }}
        >
          Request school access
        </CreateButton>
      ) : (
        <CreateButton
          icon={<MagicStarsSvg />}
          type="primary"
          loading={isLoading}
          onClick={e => {
            debounceEventIfNotTrusted({
              cb: () => {
                if (!isReady) {
                  showAuthGuard();
                  return;
                }
                if (
                  isPremiumGenerator &&
                  !tenantPlan.isPremium() &&
                  !tenantPlan.isEnterprise()
                ) {
                  metrics.logEvent('Upgrade.Clicked', {
                    location: upgradeLocation,
                    generatorId,
                  });
                  promptPaywall();
                  return;
                } else {
                  handleSubmit();
                }
              },
              isTrusted: e.isTrusted,
            });
          }}
          disabled={!isFormEnabled}
        >
          Customize now
        </CreateButton>
      )}
    </FormContainer>
  );
};

const CreateButton = styled(Button)<{ $color?: string }>`
  flex-direction: row-reverse;
  margin: 10px auto 0px auto;
  svg {
    height: 20px;
    width: 20px;
    margin-left: 10px;
    margin-right: 0px;
  }
  @media (max-width: 600px) {
    margin: 10px auto;
  }

  ${({ $color }) =>
    $color &&
    `
    background-color: ${$color};

    &:hover {
      background-color: ${$color};
    }

    &:focus {
      background-color: ${$color};
    }
  `}
`;

const GridContainer = styled.div`
  display: grid;
  width: 100%;
  gap: 14px;
  grid-template-columns: 1fr;
`;

const OptionalInputCollapsePanel = styled(Collapse.Panel)`
  background-color: transparent;
  margin-top: 10px;
`;

const OptionalInputCollapse = styled(Collapse)`
  background-color: transparent;
  .ant-collapse-header {
    padding: 0 !important;
  }
  .ant-collapse-content-box {
    padding: 0;
  }
`;

export const useGeneratorFormState = (
  promptInputs?: PromptInput[],
  existingFormState?: GeneratorFormState,
) => {
  const [formState, setFormState] = useState<GeneratorFormState>(
    promptInputs
      ? generateFormState(promptInputs)
      : existingFormState
      ? existingFormState
      : {},
  );

  useEffect(() => {
    if (existingFormState) {
      setFormState(existingFormState);
    }
  }, [existingFormState]);

  const handleInputChange = useCallback(
    (
      inputId: string,
      value: string | number | { filePath: string; fileType: string },
    ) => {
      if (typeof value === 'string' || typeof value === 'number') {
        setFormState({
          ...formState,
          [inputId]: value,
        });
        return;
      }
      setFormState({
        ...formState,
        [inputId]: value,
      });
    },
    [formState],
  );

  return {
    handleInputChange,
    formState,
  };
};

const ErrorMessage = styled.h4`
  font-weight: bold;
  width: 100%;
  text-align: center;
  margin-top: 20px;
  font-size: 18px;

  p {
    margin-top: 10px;
    font-size: 14px;
  }
`;

export default GeneratorForm;

const FormContainer = styled.div``;

const FileUploadArea = ({
  uploadedFile,
  setUploadedFile,
  isUploadingFile,
  isErrorUploadingFile,
  handleFileSelected,
  resetFileUpload,
  fileSizeExceeded,
  label = 'Upload Slides',
}: {
  uploadedFile: { filePath: string; fileType: string } | undefined;
  setUploadedFile: (
    uploadedFile:
      | {
          filePath: string;
          fileType: string;
        }
      | undefined,
  ) => void;
  isUploadingFile: boolean;
  isErrorUploadingFile: boolean;
  handleFileSelected: (file: File) => void;
  resetFileUpload: () => void;
  fileSizeExceeded: boolean;
  label?: string;
}) => {
  const { t } = useTranslation('Host');
  const [fileName, setFileName] = useState<string | undefined>();

  useEffect(() => {
    if (uploadedFile) {
      //Cut off the first section of the file path to give the name of the file
      const index = uploadedFile.filePath.indexOf('_');

      if (index !== -1) {
        const findFileName = uploadedFile.filePath.substring(index + 1);
        setFileName(findFileName);
      }
    }
  }, [uploadedFile]);

  return (
    <>
      {!(isUploadingFile || uploadedFile || isErrorUploadingFile) && (
        <div
          style={{
            width: '100%',
          }}
        >
          <p style={{ fontSize: 12, marginBottom: 4 }}>{label}</p>

          {fileSizeExceeded && (
            <ErrorMessage style={{ fontSize: 12, textAlign: 'left' }}>
              {t(
                'fileSizeExceeded',
                'Oops looks like this presentation is too big. Please try uploading a file smaller than 100MB',
              )}
            </ErrorMessage>
          )}
          <PresentationFileSelector onFileSelected={handleFileSelected} />
        </div>
      )}
      {(isUploadingFile || uploadedFile || isErrorUploadingFile) && (
        <div
          style={{
            alignItems: 'space-beetween',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {isUploadingFile && (
            <div
              style={{
                gridColumn: '1/3',
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Loader />
              <h3 style={{ width: '100%', textAlign: 'center' }}>{t('Uploading')}</h3>
            </div>
          )}
          {isErrorUploadingFile && (
            <div
              style={{
                width: '100%',
              }}
            >
              <IconWrapper height="100px" width="100px">
                <FoldedHandsSVG />
              </IconWrapper>
              <h3 style={{ width: '100%', textAlign: 'center' }}>
                {t('Failed to upload')}
              </h3>
              <p style={{ textAlign: 'center' }}>
                {t(
                  'Make sure the file is correct format, not corrupted and there is no password protection',
                )}
              </p>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  type="primary"
                  onClick={() => {
                    resetFileUpload();
                    setUploadedFile(undefined);
                  }}
                >
                  {t('Please try again')}
                </Button>
              </div>
            </div>
          )}
          {uploadedFile && (
            <div
              style={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 5,
                alignItems: 'center',
              }}
            >
              <h3 style={{ margin: 0, width: '100%', wordBreak: 'break-all' }}>
                {fileName}{' '}
                <span style={{ fontSize: 10, display: 'block' }}>
                  {t('successfully uploaded')}
                </span>
              </h3>
              <Button type="ghost" onClick={() => setUploadedFile(undefined)}>
                {t('Change')}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};
const useSinglePromptinputSuggestion = ({
  promptInput,
  promptInputSuggestions = [],
  value,
}: {
  promptInput: PromptInput;
  promptInputSuggestions?: PromptInput[];
  value?: string | number | { filePath: string; fileType: string };
}) => {
  const [suggestionToRender, setSuggestionToRender] = useState<string | undefined>(
    undefined,
  );
  const intervalRef = useRef<NodeJS.Timer>();
  const [renderedSuggestion, setRenderedSuggestion] = useState<string>('');
  const intervalTime = useMemo(() => {
    if (suggestionToRender && suggestionToRender?.length > 10) {
      return Math.max(1000 / suggestionToRender.length, 10);
    }
    return 100;
  }, [suggestionToRender]);

  // when the prompt input changes, we need to change the suggestion
  useEffect(() => {
    // if the prompt input is not short or long text, we don't need to render the placeholder
    if (promptInput.type !== 'shortText' && promptInput.type !== 'longText') return;

    // if the prompt input has a value, we don't need to render the placeholder
    if (value) return;

    const newSuggestion = promptInputSuggestions?.find(p => p.id === promptInput.id);
    if (newSuggestion) {
      setRenderedSuggestion('');
      setSuggestionToRender(newSuggestion.value);
      return;
    }

    // if there is no suggestion, we remove the placeholder
    if (promptInputSuggestions && promptInputSuggestions.length > 0) {
      setRenderedSuggestion('');
      setSuggestionToRender(undefined);
      return;
    }
  }, [promptInput, promptInputSuggestions, value]);

  // when the suggestion changes, we need to create the dynamic writing experience and thus render the placeholder
  useEffect(() => {
    if (intervalRef?.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      // this logic makes the rendered placeholder string look likes its being typed
      if (suggestionToRender) {
        setRenderedSuggestion(e => {
          return suggestionToRender?.slice(0, e.length + 1);
        });
      }
    }, intervalTime);

    return () => {
      if (intervalRef?.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [intervalTime, renderedSuggestion, suggestionToRender]);
  return renderedSuggestion;
};

const usePromptInputSuggestions = ({
  inputSuggestions,
  formState,
  promptInputs,
}: {
  inputSuggestions?: PromptInput[][];
  formState: GeneratorFormState | undefined;
  promptInputs: PromptInput[];
}) => {
  /**
   * used for finding the current input suggestion to render
   * */

  const intervalRef = useRef<NodeJS.Timer>();
  const [selectedInputSuggestionIndex, setSelectedInputSuggestionIndex] = useState(0);
  // makes a interval that changes the placeholder every 4 seconds
  useEffect(() => {
    if (!inputSuggestions) return;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // if we have filled in all the text inputs, we don't need to render the placeholder
    if (formState) {
      const textPromptInputs = promptInputs.filter(
        e => e.type === 'shortText' || e.type === 'longText',
      );
      if (textPromptInputs.every(p => formState[p.id])) {
        return;
      }
    }

    intervalRef.current = setInterval(() => {
      setSelectedInputSuggestionIndex(i => (i + 1) % inputSuggestions.length);
    }, 3000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [selectedInputSuggestionIndex, inputSuggestions, formState, promptInputs]);

  const currentInputSuggestions = useMemo(() => {
    if (!inputSuggestions) return [];
    return inputSuggestions[selectedInputSuggestionIndex];
  }, [inputSuggestions, selectedInputSuggestionIndex]);

  return currentInputSuggestions;
};
